import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Grade as GradeIcon,
  CheckCircle as SubmittedIcon,
  Replay as ReturnIcon,
} from '@mui/icons-material';
import ConfirmationModal from './ConfirmationModal';

interface Field {
  id: string;
  type: 'text_line' | 'textarea' | 'checkbox';
  x_pct: number;
  y_pct: number;
  width_pct: number;
  height_pct: number;
  label: string;
}

interface PageData {
  page_number: number;
  background_image_url: string;
  image_width: number;
  image_height: number;
  fields: Field[];
}

interface InteractiveResponseViewerProps {
  open: boolean;
  responseId: number;
  studentName: string;
  onClose: () => void;
  onGraded?: () => void;
}

const InteractiveResponseViewer: React.FC<InteractiveResponseViewerProps> = ({
  open,
  responseId,
  studentName,
  onClose,
  onGraded,
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { isMobile } = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pages, setPages] = useState<PageData[]>([]);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [documentName, setDocumentName] = useState('');
  const [responseStatus, setResponseStatus] = useState('');
  const [submittedAt, setSubmittedAt] = useState('');

  const [grade, setGrade] = useState('');
  const [pointsEarned, setPointsEarned] = useState('');
  const [pointsPossible, setPointsPossible] = useState('100');
  const [feedback, setFeedback] = useState('');
  const [saving, setSaving] = useState(false);
  const [returning, setReturning] = useState(false);
  const [returnConfirm, setReturnConfirm] = useState(false);

  useEffect(() => {
    if (open && responseId) {
      loadResponseDetail();
    }
  }, [open, responseId]);

  const loadResponseDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await axios.get(
        `${API_BASE_URL}/api/interactive-responses/${responseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPages(resp.data.pages || []);
      setDocumentName(resp.data.document_name || '');
      setResponses(resp.data.response?.responses || {});
      setResponseStatus(resp.data.response?.status || '');
      setSubmittedAt(resp.data.response?.submitted_at || '');

      setGrade(resp.data.response?.grade || '');
      setPointsEarned(resp.data.response?.points_earned?.toString() || '');
      setPointsPossible(resp.data.response?.points_possible?.toString() || '100');
      setFeedback(resp.data.response?.teacher_feedback || '');
    } catch (err: any) {
      setError(err.response?.data?.error || t('interactiveGrading.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGrade = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/interactive-responses/${responseId}/grade`,
        {
          grade: grade || null,
          points_earned: pointsEarned ? parseFloat(pointsEarned) : null,
          points_possible: pointsPossible ? parseFloat(pointsPossible) : null,
          teacher_feedback: feedback || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onGraded) onGraded();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || t('interactiveGrading.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleReturnForRevision = async () => {
    setReturning(true);
    setReturnConfirm(false);
    try {
      await axios.post(
        `${API_BASE_URL}/api/interactive-responses/${responseId}/return`,
        { feedback: feedback || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onGraded) onGraded();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || t('interactiveGrading.returnError'));
    } finally {
      setReturning(false);
    }
  };

  const formatDate = (iso: string) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { maxHeight: isMobile ? '100vh' : '95vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">
              {studentName} — {documentName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, alignItems: 'center' }}>
              {responseStatus === 'submitted' && (
                <Chip
                  icon={<SubmittedIcon />}
                  label={t('interactiveGrading.submitted')}
                  color="success"
                  size="small"
                />
              )}
              {responseStatus === 'returned' && (
                <Chip
                  icon={<ReturnIcon />}
                  label={t('interactiveGrading.returned')}
                  color="warning"
                  size="small"
                />
              )}
              {submittedAt && (
                <Typography variant="caption" color="text.secondary">
                  {formatDate(submittedAt)}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} aria-label={t('common.close')}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: '100%' }}>
            {/* Left: Document with student answers */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2, minWidth: 0 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t('interactiveGrading.studentWork')}
              </Typography>
              {pages.map(page => (
                <Paper
                  key={page.page_number}
                  elevation={1}
                  sx={{ mb: 2, position: 'relative', overflow: 'hidden' }}
                >
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <img
                      src={page.background_image_url}
                      alt={`Page ${page.page_number}`}
                      style={{ width: '100%', display: 'block' }}
                      draggable={false}
                    />
                    {page.fields.map(field => (
                      <Box
                        key={field.id}
                        sx={{
                          position: 'absolute',
                          left: `${field.x_pct}%`,
                          top: `${field.y_pct}%`,
                          width: `${field.width_pct}%`,
                          height: `${field.height_pct}%`,
                          display: 'flex',
                          alignItems: field.type === 'text_line' ? 'center' : 'flex-start',
                        }}
                      >
                        {field.type === 'checkbox' ? (
                          <input
                            type="checkbox"
                            checked={responses[field.id] === 'true'}
                            disabled
                            aria-label={field.label || field.id}
                            style={{ width: '100%', height: '100%' }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              background: 'rgba(232,245,233,0.9)',
                              border: '1px solid #a5d6a7',
                              borderRadius: '3px',
                              px: '4px',
                              py: '2px',
                              fontSize: 'clamp(9px, 1.2vw, 14px)',
                              lineHeight: 1.3,
                              overflow: 'hidden',
                              color: '#1b5e20',
                              fontWeight: 500,
                              whiteSpace: field.type === 'text_line' ? 'nowrap' : 'pre-wrap',
                              display: 'flex',
                              alignItems: field.type === 'text_line' ? 'center' : 'flex-start',
                            }}
                          >
                            {responses[field.id] || ''}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              ))}
            </Box>

            {/* Right: Grading panel */}
            <Box
              sx={{
                width: { xs: '100%', md: 320 },
                flexShrink: 0,
                borderLeft: { md: '1px solid' },
                borderColor: { md: 'divider' },
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                <GradeIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {t('interactiveGrading.gradeTitle')}
              </Typography>

              <Divider />

              {/* Score -- primary input */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label={t('interactiveGrading.score')}
                  value={pointsEarned}
                  onChange={e => setPointsEarned(e.target.value)}
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ flex: 1 }}
                  aria-label={t('interactiveGrading.score')}
                  autoFocus
                />
                <Typography variant="h6" color="text.secondary">/</Typography>
                <TextField
                  label={t('interactiveGrading.outOf')}
                  value={pointsPossible}
                  onChange={e => setPointsPossible(e.target.value)}
                  size="small"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  sx={{ flex: 1 }}
                  aria-label={t('interactiveGrading.outOf')}
                />
              </Box>

              {/* Live percentage preview */}
              {pointsEarned && pointsPossible && parseFloat(pointsPossible) > 0 && (
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color={
                      (parseFloat(pointsEarned) / parseFloat(pointsPossible)) * 100 >= 70
                        ? 'success.main'
                        : (parseFloat(pointsEarned) / parseFloat(pointsPossible)) * 100 >= 50
                          ? 'warning.main'
                          : 'error.main'
                    }
                  >
                    {Math.round((parseFloat(pointsEarned) / parseFloat(pointsPossible)) * 100)}%
                  </Typography>
                </Box>
              )}

              <TextField
                label={t('interactiveGrading.feedback')}
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                multiline
                rows={3}
                fullWidth
                size="small"
                placeholder={t('interactiveGrading.feedbackPlaceholder')}
                aria-label={t('interactiveGrading.feedback')}
              />

              {/* Letter grade -- optional, secondary */}
              <TextField
                label={t('interactiveGrading.letterGradeOptional')}
                value={grade}
                onChange={e => setGrade(e.target.value)}
                size="small"
                fullWidth
                placeholder="A, B+, etc."
                aria-label={t('interactiveGrading.letterGradeOptional')}
              />

              {error && (
                <Alert severity="error" onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {responseStatus === 'submitted' && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={returning ? <CircularProgress size={18} /> : <ReturnIcon />}
                  onClick={() => setReturnConfirm(true)}
                  disabled={saving || returning}
                  fullWidth
                  aria-label={t('interactiveGrading.returnForRevision')}
                >
                  {t('interactiveGrading.returnForRevision')}
                </Button>
              )}

              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} /> : <GradeIcon />}
                onClick={handleSaveGrade}
                disabled={saving || returning || (!pointsEarned && !grade)}
                fullWidth
                size="large"
                aria-label={t('interactiveGrading.saveGrade')}
              >
                {t('interactiveGrading.saveGrade')}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <ConfirmationModal
        isOpen={returnConfirm}
        title={t('interactiveGrading.returnConfirmTitle')}
        message={t('interactiveGrading.returnConfirmMessage')}
        confirmText={t('interactiveGrading.returnForRevision')}
        cancelText={t('interactive.cancel')}
        onConfirm={handleReturnForRevision}
        onCancel={() => setReturnConfirm(false)}
      />
    </Dialog>
  );
};

export default InteractiveResponseViewer;
